/* Lesen und Schreiben der Spielerakten.
 *
 * Zur Erinnerung an das Datenmodell: Die Tabelle hat fünf Spalten
 * (id, first_name, last_name, status, data). Alles Übrige steckt im
 * JSONB-Feld `data`. Name und Status stehen doppelt — als Spalte für
 * Sortierung und Datenbankregeln, und im Datenblock. Beim Schreiben werden
 * beide gesetzt, beim Lesen gewinnt die Spalte.
 *
 * Klubregister: players.current_club_id (Verweis auf football_clubs) ist eine echte Spalte, nicht Teil des
 * Datenblocks. Sie gibt es erst nach supabase/klubregister-4-spalten.sql; die App läuft davor und danach
 * (clubColumns.js): Lesen ohne die Spalte ergibt currentClubId = null (Rückfall auf den Text), Schreiben
 * versucht die Spalte und fällt bei "Spalte unbekannt" auf das Schreiben ohne sie zurück.
 *
 * ACHTUNG beim Speichern: savePlayer schreibt den KOMPLETTEN Datenblock.
 * Zwei Personen, die denselben Spieler gleichzeitig offen haben, überschreiben
 * sich vollständig — nicht feldweise. Das ist bekannt und derzeit bewusst
 * akzeptiert (siehe TODO.md, "Schutz gegen gleichzeitiges Bearbeiten").
 * Deshalb: kein automatisches Speichern im Hintergrund, nur auf Nutzeraktion.
 */

import { supabase } from './client.js';
import { ensurePlayerShape, stripTransient } from '../domain/playerShape.js';
import { withBirthYear } from '../domain/birthYear.js';
import { withAgeGroup } from '../domain/ageGroups.js';
import { noteClubColumns, writeWithClubColumns } from './clubColumns.js';
import {
  PLAYERS_SOURCE_PUBLIC, PLAYERS_SOURCE_STAFF, PUBLIC_COLUMNS, PUBLIC_COLUMNS_LEGACY, PUBLIC_COLUMNS_WITH_AGE,
  PUBLIC_COLUMNS_WITH_MATERIAL, publicRowToPlayer, staffRowToPlayer, isMissingBirthDateColumn, isMissingAgeGroupColumn,
  isMissingMaterialColumn,
} from '../domain/playerPublic.js';

/**
 * Spieler laden. `source` kommt aus domain/playerPublic.js (playersReadSource):
 *   'players'         ganze Zeilen samt Datenblock (admin, board, data, staff)
 *   'players_public'  nur die öffentlichen Felder der View (guest)
 * Die Datenbank erzwingt es ebenfalls: guest liest players nicht mehr
 * (players_select), und die View liefert nur 21 Spalten.
 */
export async function loadPlayers({ source = 'players' } = {}) {
  if (!supabase) return [];

  if (source === PLAYERS_SOURCE_PUBLIC) {
    const query = (columns) => supabase
      .from(PLAYERS_SOURCE_PUBLIC)
      .select(columns.join(','))
      .order('last_name', { ascending: true })
      .order('first_name', { ascending: true });
    let pub = await query(PUBLIC_COLUMNS_WITH_MATERIAL);
    /* Die View hat match_videos und traits erst nach supabase/gaeste-material.sql. Bis dahin mit den 22 Spalten
       anfragen: Gäste sehen im Material-Reiter dann nur das Highlight, keine Matches, keine Schlagwörter, keinen Fehler. */
    if (pub.error && isMissingMaterialColumn(pub.error)) {
      console.warn('[players] View ohne match_videos/traits — Abfrage ohne Material (gaeste-material.sql noch nicht ausgeführt?)');
      pub = await query(PUBLIC_COLUMNS_WITH_AGE);
    }
    /* Die View hat age_group erst nach supabase/gaeste-altersklasse.sql. Bis dahin ohne die Spalte anfragen: Gäste
       sehen dann keinen Altersklasse-Chip, aber die Liste fällt nicht aus. */
    if (pub.error && isMissingAgeGroupColumn(pub.error)) {
      console.warn('[players] View ohne age_group — Abfrage ohne Altersklasse (SQL noch nicht ausgeführt?)');
      pub = await query(PUBLIC_COLUMNS);
    }
    /* Die View hat birth_date erst nach supabase/gaeste-geburtsdatum.sql. Bis dahin ohne die Spalte
       anfragen: Gäste sehen dann wie bisher das Geburtsjahr, statt dass die Liste ausfällt. */
    if (pub.error && isMissingBirthDateColumn(pub.error)) {
      console.warn('[players] View ohne birth_date — Abfrage ohne Geburtsdatum (SQL noch nicht ausgeführt?)');
      pub = await query(PUBLIC_COLUMNS_LEGACY);
    }
    if (pub.error) {
      console.error('[players] Laden (öffentliche Ansicht) fehlgeschlagen:', pub.error.message);
      throw new Error('loadFailed');
    }
    return (pub.data || []).map(publicRowToPlayer);
  }

  if (source === PLAYERS_SOURCE_STAFF) {
    const staff = await supabase
      .from(PLAYERS_SOURCE_STAFF)
      .select('*')
      .order('last_name', { ascending: true })
      .order('first_name', { ascending: true });
    if (!staff.error) {
      if (staff.data && staff.data.length) noteClubColumns('players', staff.data[0]);
      return (staff.data || []).map(staffRowToPlayer);
    }
    /* Die Ansicht gibt es noch nicht (Schritt 1 der Staff-Sichtbarkeit nicht ausgeführt): wie bisher aus
       players lesen. Jeder andere Fehler ist ein echter Ladefehler — nicht still auf die volle Tabelle ausweichen. */
    if (!isMissingRelation(staff.error)) {
      console.error('[players] Laden (Staff-Ansicht) fehlgeschlagen:', staff.error.message);
      throw new Error('loadFailed');
    }
    console.warn('[players] Ansicht players_staff fehlt — Abfrage auf players (supabase/staff-sichtbarkeit-1-ansichten.sql noch nicht ausgeführt?)');
  }

  const { data, error } = await supabase
    .from('players')
    .select('*')
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[players] Laden fehlgeschlagen:', error.message);
    throw new Error('loadFailed');
  }

  if (data && data.length) noteClubColumns('players', data[0]);

  return (data || []).map((row) =>
    ensurePlayerShape({
      ...(row.data || {}),
      id: row.id,
      currentClubId: row.current_club_id || null,
      firstName: row.first_name || (row.data && row.data.firstName) || '',
      lastName: row.last_name || (row.data && row.data.lastName) || '',
      status: row.status || (row.data && row.data.status) || 'trial',
    })
  );
}

/** Fehlt die Tabelle oder Ansicht? Postgres 42P01, PostgREST PGRST205 (Schema-Cache) oder der Text. */
function isMissingRelation(error) {
  if (!error) return false;
  return error.code === '42P01' || error.code === 'PGRST205' || /does not exist|schema cache/i.test(String(error.message || ''));
}

export async function savePlayer(input) {
  if (!supabase) throw new Error('notConnected');
  /* Ein Objekt aus der öffentlichen Ansicht (Gast) ist unvollständig: Speichern würde den
     Datenblock mit leeren Vorgaben überschreiben. Gäste dürfen ohnehin nichts schreiben. */
  if (input._publicOnly || input._staffView) throw new Error('notAllowed');

  /* jg aus dob nachziehen, damit gespeicherter Jahrgang und Geburtsdatum nie auseinanderlaufen. */
  const player = withBirthYear(input);
  const clean = stripTransient(player);
  const row = {
    first_name: player.firstName,
    last_name: player.lastName,
    status: player.status,
    data: clean,
  };

  /* Verweis auf den Verein: in die Spalte, nie in den Datenblock (stripTransient hat ihn entfernt) */
  const withClub = { ...row, current_club_id: player.currentClubId || null };

  if (player._isNew || !player.id) {
    const { data, error } = await writeWithClubColumns('players', withClub, row, (r) =>
      supabase.from('players').insert(r).select().single()
    );
    if (error) {
      console.error('[players] Anlegen fehlgeschlagen:', error.message);
      throw new Error('saveFailed');
    }
    return { ...player, id: data.id, _isNew: false };
  }

  const { error } = await writeWithClubColumns('players', withClub, row, (r) =>
    supabase.from('players').update(r).eq('id', player.id)
  );
  if (error) {
    console.error('[players] Speichern fehlgeschlagen:', error.message);
    throw new Error('saveFailed');
  }
  return player;
}

/**
 * Spieler endgültig löschen. Die Datenbank nimmt player_contracts, player_contract_files und
 * player_transfers mit (on delete cascade). Dateien und Meldungen NICHT — das erledigt
 * domain/playerDeletion.js (deletePlayerFlow), der einzige vorgesehene Aufrufer. Nicht direkt
 * aus der Oberfläche aufrufen: ohne den Ablauf blieben Dateien und Meldungen zurück.
 *
 * Liefert die Zahl gelöschter Zeilen. Eine verweigernde Regel (RLS) meldet KEINEN Fehler, sie
 * löscht still 0 Zeilen — deshalb zählt der Aufrufer (0 = nicht erlaubt oder nicht vorhanden).
 */
export async function deletePlayer(id) {
  if (!supabase) throw new Error('notConnected');
  const { data, error } = await supabase.from('players').delete().eq('id', id).select('id');
  if (error) {
    console.error('[players] Löschen fehlgeschlagen:', error.message);
    /* Der Auslöser players_log_deletion bricht das Löschen ab, wenn das Löschprotokoll
       (player_deletions) nicht geschrieben werden kann (fail closed, supabase/loeschprotokoll.sql). */
    throw new Error(/player_deletion_log_failed/.test(error.message || '') ? 'deleteLogFailed' : 'deleteFailed');
  }
  return (data || []).length;
}

/**
 * Altersklasse für mehrere Spieler setzen (Mehrfachzuordnung der Auswahlleiste). Liest je Spieler den FRISCHEN
 * Datenblock aus der Datenbank, ändert nur den Schlüssel ageGroup (domain/ageGroups.js withAgeGroup) und schreibt
 * ihn zurück — nicht aus dem Speicher der App, damit zwischenzeitliche Änderungen anderer nicht überschrieben
 * werden (nur das kurze Fenster zwischen Lesen und Schreiben bleibt, wie bei jedem Speichern).
 *
 * Liefert die IDs, die die Datenbank tatsächlich geändert hat. Eine verweigernde Regel (RLS) meldet keinen Fehler,
 * sondern ändert 0 Zeilen — deshalb zählt die Rückgabe. Ein Fehler bei einem Spieler (z. B. die Vertragssperre bei
 * Altbestand) lässt die übrigen weiterlaufen; er steht in der Console, der Spieler fehlt in der Rückgabe.
 */
export async function setAgeGroupFor(ids, value) {
  if (!supabase) throw new Error('notConnected');
  if (!ids || !ids.length) return [];
  const { data: rows, error } = await supabase.from('players').select('id, data').in('id', ids);
  if (error) {
    console.error('[players] Altersklasse: Lesen fehlgeschlagen:', error.message);
    throw new Error('loadFailed');
  }
  const changed = [];
  for (const row of rows || []) {
    const { data: hit, error: werr } = await supabase
      .from('players')
      .update({ data: withAgeGroup(row.data, value) })
      .eq('id', row.id)
      .select('id');
    if (werr) {
      console.error('[players] Altersklasse: Speichern fehlgeschlagen:', werr.message);
      continue;
    }
    if (hit && hit.length) changed.push(row.id);
  }
  return changed;
}
